import type { TFunction } from 'i18next';
import { z } from 'zod';

export type LoginFormData = {
  email: string;
  password: string;
};

export function getLoginSchema(t: TFunction) {
  return z.object({
    email: z.string().min(1, t('validation.emailRequired')).email(t('validation.emailInvalid')),
    password: z.string().min(1, t('validation.passwordRequired')),
  });
}

export type RegisterFormData = {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
};

export function getRegisterSchema(t: TFunction) {
  return z
    .object({
      name: z.string().min(1, t('validation.nameRequired')).min(2, t('validation.nameMinLength')),
      email: z.string().min(1, t('validation.emailRequired')).email(t('validation.emailInvalid')),
      password: z
        .string()
        .min(1, t('validation.passwordRequired'))
        .min(8, t('validation.passwordMinLength'))
        .regex(/[a-z]/, t('validation.passwordLowercase'))
        .regex(/[A-Z]/, t('validation.passwordUppercase'))
        .regex(/[0-9]/, t('validation.passwordNumber')),
      confirmPassword: z.string().min(1, t('validation.confirmPasswordRequired')),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('validation.passwordMismatch'),
      path: ['confirmPassword'],
    });
}

export type ForgotPasswordFormData = {
  email: string;
};

export function getForgotPasswordSchema(t: TFunction) {
  return z.object({
    email: z.string().min(1, t('validation.emailRequired')).email(t('validation.emailInvalid')),
  });
}

export type ResetPasswordFormData = {
  password: string;
  confirmPassword: string;
};

export function getResetPasswordSchema(t: TFunction) {
  return z
    .object({
      password: z
        .string()
        .min(1, t('validation.passwordRequired'))
        .min(8, t('validation.passwordMinLength'))
        .regex(/[a-z]/, t('validation.passwordLowercase'))
        .regex(/[A-Z]/, t('validation.passwordUppercase'))
        .regex(/[0-9]/, t('validation.passwordNumber')),
      confirmPassword: z.string().min(1, t('validation.confirmPasswordRequired')),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('validation.passwordMismatch'),
      path: ['confirmPassword'],
    });
}

export function getPasswordStrength(password: string): {
  score: number;
  strengthKey: 'strengthWeak' | 'strengthMedium' | 'strengthStrong';
  color: string;
} {
  let score = 0;

  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;

  if (score <= 2) {
    return { score, strengthKey: 'strengthWeak', color: 'bg-red-500' };
  } else if (score <= 4) {
    return { score, strengthKey: 'strengthMedium', color: 'bg-yellow-500' };
  } else {
    return { score, strengthKey: 'strengthStrong', color: 'bg-green-500' };
  }
}
